import * as React from 'react';
import { ChevronDown, X } from 'lucide-react';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { cn } from '../lib/utils.js';
import { parseDateStr, toDateStr } from '../lib/date-string.js';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  CALENDAR_POPOVER_POSITION_PROPS,
} from './popover.js';
import { Calendar } from './calendar.js';
import {
  PILL_BUTTON_BASE,
  PILL_BUTTON_IDLE,
  PILL_BUTTON_ACTIVE,
  PILL_VALUE_CLASS,
  PILL_VALUE_IDLE_CLASS,
  PillWrapper,
} from './filter-pill-primitives.js';
import type { DateRangeFilterField, FilterPanelLabels } from './filter-pill-primitives.js';

// ─── DateRangePill ────────────────────────────────────────────

export interface DateRangePillProps {
  field: DateRangeFilterField;
  /** value[0] = from date, value[1] = to date (YYYY-MM-DD strings) */
  selected: string[];
  onChange: (id: string, values: string[]) => void;
  onClear: (id: string) => void;
  isLoading?: boolean;
  labels: FilterPanelLabels;
  autoOpen?: boolean;
  onAutoOpened?: () => void;
}

export function DateRangePill({
  field,
  selected,
  onChange,
  onClear,
  isLoading,
  labels,
  autoOpen,
  onAutoOpened,
}: DateRangePillProps) {
  const [open, setOpen] = React.useState(false);
  const fromStr = selected[0] ?? '';
  const toStr = selected[1] ?? '';
  const fromDate = parseDateStr(fromStr);
  const toDate = parseDateStr(toStr);
  const isActive = fromStr !== '' || toStr !== '';

  const dateRange: DateRange | undefined = isActive ? { from: fromDate, to: toDate } : undefined;

  const { locale } = field;
  const formatDate = (date: Date) => format(date, 'd MMM y', locale ? { locale } : undefined);

  React.useEffect(() => {
    if (!autoOpen || open) return;
    // Delay opening so the AddFilterButton popover fully closes first,
    // otherwise the focus shift immediately dismisses this popover.
    const timer = window.setTimeout(() => {
      setOpen(true);
      onAutoOpened?.();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [autoOpen, open]);

  function handleSelect(range: DateRange | undefined) {
    const from = range?.from ? toDateStr(range.from) : '';
    const to = range?.to ? toDateStr(range.to) : '';
    // If both are empty, write [] to match handleClear behaviour
    if (from === '' && to === '') {
      onChange(field.id, []);
    } else {
      onChange(field.id, [from, to]);
    }
  }

  function handleClear(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    onClear(field.id);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PillWrapper label={field.label} showLabel={isActive}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid={`filter-trigger-${field.id}`}
            disabled={isLoading}
            className={cn(PILL_BUTTON_BASE, isActive ? PILL_BUTTON_ACTIVE : PILL_BUTTON_IDLE)}
          >
            {isActive ? (
              <>
                <span className={cn(PILL_VALUE_CLASS, 'text-[11px] text-foreground')}>
                  {}
                  {fromDate ? formatDate(fromDate) : '...'}
                  {}
                  {' - '}
                  {}
                  {toDate ? formatDate(toDate) : '...'}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={labels.getClearAriaLabel(field.id)}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClear(e);
                  }}
                  className="shrink-0 hover:opacity-70 rounded-full p-0.5"
                >
                  <X className="h-3 w-3 text-foreground/60" />
                </span>
              </>
            ) : (
              <>
                <span className={cn(PILL_VALUE_IDLE_CLASS, 'text-muted-foreground')}>
                  {field.label}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </>
            )}
          </button>
        </PopoverTrigger>
      </PillWrapper>
      {}
      <PopoverContent className="w-auto p-0" align="start" {...CALENDAR_POPOVER_POSITION_PROPS}>
        <Calendar
          mode="range"
          defaultMonth={fromDate}
          selected={dateRange}
          onSelect={handleSelect}
          numberOfMonths={2}
          {...(locale ? { locale } : {})}
        />
      </PopoverContent>
      {}
    </Popover>
  );
}
